// @ts-strict-ignore
import type {Component, PanelLifecycleContext} from 'panel';

import type {Store, SubscriptionCallbackParams} from 'common/report/stores/store';
import type {Section} from 'common/components/items-menu/types';
import {reporter} from 'common/report/error-reporting';
import {warnDeveloper} from 'common/report/util';
import {Subscribable} from 'common/types/context/subscribable';

/**
 * A data controller defines the way a menu fetches, presents and reacts
 * to Store changes from the different Stores it might depend on, the overall goals of a
 * data controller are:
 * - it is consumed with `getSections()` and `data`
 * - must update the component(s) when data is available to render (is loaded, or loading for instance)
 * - must perform the right Store bookkeeping on the component's lifecycle
 *
 * - [PENDING] must initialize Store values from the component `selected` attr (custom props, cohorts, temp mappings)
 */
export abstract class BaseDataController implements PanelLifecycleContext, Subscribable {
  protected connectedComponents: Set<Component<any>> = new Set();
  protected blockedComponents: Set<Component<any>> = new Set();
  protected loading = false;
  declare protected onDataUpdated: () => void;
  protected _onStoreCallback = (params: SubscriptionCallbackParams) => {
    this.readyStoreSet.add(params.store);
    const allStoresReady = this.providerStores.every((store) => this.readyStoreSet.has(store));
    if (allStoresReady) {
      this.onStoreCallback(params);
    }
  };
  protected readyStoreSet = new Set<Store<any>>();
  public data: Map<string, Obj> = null;
  private subscriptions = new Set<() => void>();

  public subscribe(callback: () => void): this {
    if (!this.hasSubscriptions()) {
      this.subscribeStores();
    }

    this.subscriptions.add(callback);
    return this;
  }

  public unsubscribe(callback: () => void): this {
    this.subscriptions.delete(callback);

    if (!this.hasSubscriptions()) {
      this.unsubscribeStores();
    }

    return this;
  }

  /**
   * Callback that's triggered on store changes. Note this will not trigger until all stores are ready and the store's
   * data version has changed
   */
  protected onStoreCallback(_meta: SubscriptionCallbackParams) {
    throw new Error(`not implemented`);
  }

  public get providerStores(): Array<Store<unknown>> {
    return [];
  }

  public fetchData(..._params) {
    throw new Error(`not implemented`);
  }

  public getSections(..._params): Section[] {
    throw new Error(`not implemented`);
  }

  public updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }

    for (const subscription of this.subscriptions) {
      subscription();
    }
  }

  private subscribeStores() {
    this.providerStores.forEach((store) => {
      store.subscribe(this._onStoreCallback);
    });
  }

  private unsubscribeStores() {
    this.providerStores.forEach((store) => {
      store.unsubscribe(this._onStoreCallback);
    });
  }

  private hasSubscriptions() {
    return this.connectedComponents.size > 0 || this.subscriptions.size > 0;
  }

  public bindToComponent(component: Component<any>) {
    if (this.connectedComponents.has(component)) {
      reporter.warn(`BaseDataController.bindToComponent: component is already connected`);
      return;
    }
    if (this.blockedComponents.has(component)) {
      return;
    }

    if (!this.hasSubscriptions()) {
      this.subscribeStores();
      this.fetchData();
    }

    this.connectedComponents.add(component);
  }

  public unbindFromComponent(component: Component<any>) {
    if (!this.connectedComponents.has(component)) {
      warnDeveloper(`BaseDataController.unbindFromComponent: component cannot be unbound because it's not connected`);
      return;
    }
    this.connectedComponents.delete(component);

    if (!this.hasSubscriptions()) {
      this.unsubscribeStores();
    }
  }

  /**
   * Due to contexts being static to a component rather than dynamically configurable, this method provides a way
   * for instances of a component to not be bound to the component.
   */
  blockUpdates(component) {
    if (this.connectedComponents.has(component)) {
      reporter.error(`BaseDataController.blockUpdates: should be called before super.connectedCallback()`);
      return;
    }
    this.blockedComponents.add(component);
  }
}
